import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { setAuthDetails, clearAuthDetails } from "../Redux/AuthSlice";
import store from "../Redux/Store";

const protocol = window.location.protocol;
const hostname = window.location.hostname;


const BASE_URL = `${protocol}//${hostname}:8014/ems`;
const Login_URL = `${protocol}//${hostname}:9004/ems`;
const MICROSERVICE_URL = `${protocol}//${hostname}:8015/ems`;

const rawAxios = axios.create();

let refreshTimer = null;

// ===== Token Helpers =====
const getAccessToken = () => localStorage.getItem("token");
const getRefreshToken = () => localStorage.getItem("refreshToken");

const getTokenExpiry = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // ms
  } catch (e) {
    console.error("Error decoding token:", e);
    return null;
  }
};

// 🔑 Context updater reference
let contextUpdater = null;
export const registerContextUpdater = (fn) => {
  contextUpdater = fn;
};

// ===== Centralized Token Setter =====
export const setTokens = (accessToken, refreshToken) => {
  // 1. Update localStorage
  localStorage.setItem("token", accessToken);
  localStorage.setItem("refreshToken", refreshToken);

  // 2. Decode token
  let decoded = null;
  try {
    decoded = jwtDecode(accessToken);
  } catch (err) {
    console.error("Failed to decode token:", err);
  }

  // 3. Update Redux
  if (decoded) {
    store.dispatch(
      setAuthDetails({
        userId: decoded.sub,
        userRole: decoded.roles,
        company: decoded.company,
        employeeId: decoded.employee,
        source: "company",
        resourceType: decoded.resourceType,
      })
    );
  }

  // 4. Update AuthContext (if registered)
  if (decoded && contextUpdater) {
    contextUpdater({
      userId: decoded.sub,
      roles: decoded.roles,
      company: decoded.company,
      employeeId: decoded.employee,
      resourceType: decoded.resourceType,
    });
  }

  // 5. Re-schedule auto refresh
  scheduleAutoRefresh();
};

// ===== Refresh Token Call =====
const refreshTokenCall = async () => {
  try {
    const response = await rawAxios.post(`${Login_URL}/refresh/token`, {
      refreshToken: getRefreshToken(),
    });
    const { token: newAccessToken, refreshToken: newRefreshToken } =
      response.data.data;

    setTokens(newAccessToken, newRefreshToken);
    console.log("✅ Token refreshed successfully");
    return newAccessToken;
  } catch (error) {
    console.error("❌ Failed to refresh token", error);
    if (error.response?.status === 401) {
      logout();
    }
  }
};

// ===== Auto Refresh Scheduler =====
const scheduleAutoRefresh = () => {
  clearTimeout(refreshTimer);
  const token = getAccessToken();
  const expiryTime = getTokenExpiry(token);
  if (!token || !expiryTime) return;
  const now = Date.now();
  const timeUntilRefresh = expiryTime - now - 1 * 60 * 1000; // refresh 1 min before expiry

  if (timeUntilRefresh <= 0) {
    console.log("🔄 Token near expiry, refreshing now...");
    refreshTokenCall().then((newToken) => {
      if (newToken) scheduleAutoRefresh();
    });
    return;
  }

  refreshTimer = setTimeout(() => {
    refreshTokenCall().then((newToken) => {
      if (newToken) scheduleAutoRefresh();
    });
  }, timeUntilRefresh);

};

// ===== Logout (clears all sources) =====
export const logout = () => {
  clearTimeout(refreshTimer);
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  store.dispatch(clearAuthDetails());
  if (contextUpdater) contextUpdater(null);
  window.location.href = "/";
};

// ===== Axios Instances =====
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const microserviceAxiosInstance = axios.create({
  baseURL: MICROSERVICE_URL,
  headers: { "Content-Type": "application/json" },
});

// ===== Attach Token =====
const attachAuthHeader = (config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
};

axiosInstance.interceptors.request.use(attachAuthHeader);
microserviceAxiosInstance.interceptors.request.use(attachAuthHeader);

// ===== Retry on 401 =====
const addResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const newToken = await refreshTokenCall();
        if (newToken) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return instance(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );
};

addResponseInterceptor(axiosInstance);
addResponseInterceptor(microserviceAxiosInstance);

export {
  axiosInstance,
  microserviceAxiosInstance,
  scheduleAutoRefresh,
  refreshTokenCall,
};

// ===== APIs =====
export const CompanyShortNameApi = (shortName) => {
  return axios.post(`${BASE_URL}/company/${shortName}/validate`);
};

export const bookDemoApi = (data) =>
  rawAxios.post(`${BASE_URL}/book-demo`, data);

export const loginApi = (data) => {
  return axios
    .post(`${Login_URL}/emsadmin/login`, data)
    .then((response) => {
      const { token, refreshToken } = response.data.data;
      setTokens(token, refreshToken); // ✅ schedule auto-refresh
      return response.data;
    });
};

export const CompanyloginApi = (data) => {
  return axios
    .post(`${Login_URL}/company/login`, data)
    .then((response) => {
      const { token, refreshToken } = response.data?.data || {};
      if (token && refreshToken) {
        setTokens(token, refreshToken); // ✅ schedule auto-refresh
      }
      return response.data;
    });
};

export const CompanyLogoutApi = () => {
  return axiosInstance
    .get(`${Login_URL}/company/logout`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Logout API error:", error);
      // Even if the API call fails, we should still clear local storage
      throw error;
    });
};


export const CandidateloginApi = (data) => {
  return axios
    .post(`${Login_URL}/candidate/login`, data)
    .then((response) => {
      const { token, refreshToken } = response.data?.data || {};
      if (token && refreshToken) {
        setTokens(token, refreshToken); // ✅ schedule auto-refresh
      }
      return response.data;
    });
};

export const sessionLogoutApi = (data) => {
  return axios.post(`${Login_URL}/company/session/out`, data)
}

export const ValidateOtp = (data) => {
  return axios.post(`${Login_URL}/validate`, data);
}

export const forgotPasswordStep1 = (data) => {
  return axios.post(`${Login_URL}/forgot/password`, data);
}

export const forgotPasswordStep2 = (data) => {
  return axios.post(`${Login_URL}/update/password`, data);
}

export const resetPassword = (data, employeeId) => {
  return axiosInstance.patch(`/company/employee/${employeeId}/password`, data);
}

// ems admin resetPassword api file
export const resetPasswordEmsAdmin = (payload) => {
  return axios.patch(`${Login_URL}/emsadmin/password/reset`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });
};

export const resendPasswordOTP = (data) => {
  return axios.post(`${Login_URL}/resend/otp`, data);
}

export const companyUserLogoutApi = (data) => {
  return axiosInstance.post(`/company/user/session/out`, data);
}

export const MarkDailyAttendanceApi = (data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/${company}/employee/attendance/daily`, data);
};

export const GetDailyAttendanceApi = (employeeId, month, year) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/employee/attendance/daily`, {
    params: { employeeId, month, year }
  });
};

export const GetAllDailyAttendanceApi = (date) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/attendance/daily/all`, {
    params: { date }
  });
};

export const UpdateDailyAttendanceApi = (id, data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.put(`/${company}/employee/attendance/daily/${id}`, data);
};

export const CompanyRegistrationApi = (data) => {
  return axiosInstance.post("/company", data);
};

// Email Config APIs
export const testEmailConfigApi = (data) => axiosInstance.post("/email/config/test", data);
export const createEmailConfigApi = (data) => axiosInstance.post("/email/config", data);
export const updateEmailConfigApi = (id, data) => axiosInstance.patch(`/email/config/${id}`, data);
export const getEmailConfigByModuleApi = (module) => axiosInstance.get(`/email/config/module/${module}`);
export const getAllEmailConfigsApi = () => axiosInstance.get("/email/configs");
export const toggleEmailConfigStatusApi = (id) => axiosInstance.patch(`/email/config/${id}/status`);
export const deleteEmailConfigApi = (id) => axiosInstance.delete(`/email/config/${id}`);

// Recipient Email Config APIs
export const createRecipientEmailConfigApi = (data) => axiosInstance.post("/notification/recipient/config", data);
export const getAllRecipientEmailConfigsApi = () => axiosInstance.get("/notification/recipient/config");
export const getRecipientEmailConfigByEventApi = (event) => axiosInstance.get(`/notification/recipient/config/${event}`);
export const updateRecipientEmailConfigApi = (id, data) => axiosInstance.patch(`/notification/recipient/config/${id}`, data);
export const deleteRecipientEmailConfigApi = (id) => axiosInstance.delete(`/notification/recipient/config/${id}`);

export const CompanyAddApi = (data) => {
  return axios.post(`${BASE_URL}/company/add`, data)
};

export const companyViewApi = async () => {
  return axiosInstance.get("/company");
};

export const companyViewByIdApi = (companyId) => {
  return axiosInstance.get(`/company/${companyId}`)
};

export const companyDetailsByIdApi = async (companyId) => {
  return axiosInstance.get(`/company/${companyId}`);
}

export const companyDeleteByIdApi = async (companyId) => {
  return axiosInstance.delete(`/company/${companyId}`);
};

export const getCompanyStatsApi = () => {
  return axiosInstance.get("/company/stats");
};

export const getOverallStatsApi = () => {
  return axiosInstance.get("/company/overallStats");
};

export const companyRolesPatchByIdAPI = async (company, data) => {
  try {
    const response = await axiosInstance.patch(`/company/${company}/roles`, data);
    return response.data;  // Return the response data for further handling in the calling function
  } catch (error) {
    // Ensure errors are propagated properly by rethrowing the error
    console.error('Error during company update:', error);  // Optional logging
    throw error;  // Rethrow the error so it can be caught in onSubmit's catch block
  }
};

export const companyUpdateByIdApi = async (companyId, data) => {
  try {
    const response = await axiosInstance.patch(`/company/${companyId}`, data);
    return response.data;  // Return the response data for further handling in the calling function
  } catch (error) {
    // Ensure errors are propagated properly by rethrowing the error
    console.error('Error during company update:', error);  // Optional logging
    throw error;  // Rethrow the error so it can be caught in onSubmit's catch block
  }
};

export const updateCompanyStatusApi = (id, status, roles = []) => {
  const body = roles && roles.length > 0 ? { roles } : undefined;
  return axiosInstance.patch(`company/${id}/status/${status}`, body);
};

export const companyPasswordUpdateById = async (companyId) => {
  axiosInstance.patch(`/company/password/${companyId}`);
}
export const CompanyValidateApi = (companyName) => {
  return axiosInstance.post(`/company/${companyName}/validate`);
};

export const EmployeeGetUnassignedApi = (companyName) => {
  return axiosInstance.get(`/${companyName}/employees/unassigned`);
};

export const EmployeeAssignBranchApi = (companyName, employeeId, branchId) => {
  return axiosInstance.patch(`/${companyName}/employee/${employeeId}/assign-branch`, { branchId });
};
export const DepartmentGetApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`${company}/department`);
}

export const DepartmentPostApi = async (departmentData) => {
  try {
    const response = await axiosInstance.post("/department", departmentData);

    // Debug the raw response
    console.log('API Response:', response.data);

    // Handle case where API returns just {path, message, data: 'success'}
    if (response.data?.data === 'success') {
      return {
        id: `temp-${Date.now()}`, // Temporary ID
        name: departmentData.name,
        companyName: departmentData.companyName,
        type: "department"
      };
    }

    // Handle case where API returns the full department
    if (response.data?.data?.name) {
      return response.data.data;
    }

    throw new Error('API returned unexpected format');
  } catch (error) {
    console.error('DepartmentPostApi error:', {
      error: error.response?.data || error.message,
      request: departmentData
    });
    throw error;
  }
};
export const DepartmentGetApiById = (departmentId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`${company}/department/${departmentId}`)
}

export const DepartmentDeleteApiById = (departmentId) => {
  const company = localStorage.getItem("companyName");
  if (!company) {
    throw new Error("Company Name is not found");
  }

  return axiosInstance.delete(`/${company}/department/${departmentId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Delete Department Error:', {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config // This will show the full request details
      });
      throw error;
    });
};
export const DepartmentPutApiById = (departmentId, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.patch(`${company}/department/${departmentId}`, data)
};

// ===== Branch APIs =====
export const BranchGetApi = () => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/branch`);
};

export const BranchGetByCompanyApi = (company) => {
  return axiosInstance.get(`/${company}/branch`);
};

export const BranchUpdateApi = (company, branchId, data) => {
  return axiosInstance.patch(`/${company}/branch/${branchId}`, data);
};

export const BranchPostApi = (data) => {
  return axiosInstance.post("/branch", data);
};

export const BranchGetApiById = (branchId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/branch/${branchId}`);
};

export const BranchPutApiById = (branchId, data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.patch(`/${company}/branch/${branchId}`, data);
};

export const BranchDeleteApiById = (branchId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${company}/branch/${branchId}`);
};


export const DesignationGetApi = async (departmentId) => {
  const company = localStorage.getItem("companyName");
  try {
    const response = await axiosInstance.get(
      `/${company}/department/${departmentId}/designation`
    );

    // Check if response exists and has data with expected structure
    if (!response?.data) {
      console.warn("No data in response");
      throw new Error("No data received from server");
    }

    // Validate response structure
    if (response.data.message !== "Success") {
      console.warn("API did not return success message");
      throw new Error("API request was not successful");
    }

    if (!Array.isArray(response.data.data)) {
      console.warn("Unexpected data format in response");
      throw new Error("Invalid data format received");
    }

    return response.data.data; // Return the array of designations

  } catch (error) {
    console.error('Designation API Error:', {
      departmentId,
      status: error.response?.status,
      url: error.config?.url,
      error: error.message,
    });
    throw error; // Re-throw the error to be caught by the thunk
  }
};

export const DesignationPostApi = (departmentId, data) => {
  return axiosInstance.post(`/department/${departmentId}/designation`, data);
};

export const DesignationGetApiById = (departmentId, designationId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance
    .get(`/${company}/department/${departmentId}/designation/${designationId}`)
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching designation by ID:', error);
      throw error;
    });
};

export const DesignationDeleteApiById = (departmentId, designationId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${company}/department/${departmentId}/designation/${designationId}`);
};

export const DesignationPutApiById = (departmentId, designationId, data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.patch(
    `/${company}/department/${departmentId}/designation/${designationId}`,
    data
  );
};

export const GetManagerEmployeesApi = () => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/employees/managers`);
};


export const EmployeeGetApi = (page = 0, size = 10, search = "", branchId = "", status = "") => {
  const company = localStorage.getItem("companyName");

  const params = { page, size };
  if (search && search.trim() !== "") {
    params.search = search;
  }
  if (branchId && branchId.trim() !== "") {
    params.branchId = branchId;
  }
  if (status && status.trim() !== "") {
    params.status = status;
  }

  return axiosInstance.get(`/${company}/employee`, {
    params,
  });
};

export const getEmployeeStatsApi = (companyName, branchId, year) => {
  return axiosInstance.get(`/${companyName}/employee/stats`, {
    params: {
      branchId: branchId,
      year: year
    }
  });
};

// tax consultant employee update api
export const EmployeeUpdateApi = (employeeId, data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.patch(`/${company}/employee/${employeeId}`, data);
};

export const EmployeeUpdatePFStatusApi = (employeeId, isPFEmployee) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.patch(`/${company}/employee/${employeeId}/pf-status`, null, {
    params: { isPFEmployee },
  });
};

export const EmployeePFDetailsGetAPI = (branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;
  return axiosInstance.get(`/${company}/employee/accounts`, { params });
};

// PF Api Files
export const EmployeePFComparingAPI = (month, year, formData, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(
    `/${company}/pf/comparing`,
    formData,
    {
      params,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
};

export const RegisterPFEmployeeAPI = (employeeData) => {
  const company = localStorage.getItem("companyName");
  return microserviceAxiosInstance.post(`/${company}/employees/pf/register`, employeeData);
};

export const SubmitPFForProcessingAPI = (month, year, formData, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(`/${company}/employees/pf`, formData, {
    params,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const GetPFForMonthAndYearAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (month) params.month = month;
  if (year) params.year = year;
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/employee/account`, { params });
};

export const AddSinglePFEmployeeAPI = (data, branchId) => {
  const company = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;
  return microserviceAxiosInstance.post(`/${company}/employee/pf`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const AddSinglePTEmployeeAPI = (data, branchId) => {
  const company = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;
  return microserviceAxiosInstance.post(`/${company}/employee/pt`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const AddSingleTDSEmployeeAPI = (data, branchId) => {
  const company = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;
  return microserviceAxiosInstance.post(`/${company}/employee/tds`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const AddPFReceiptsAPI = (formData, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;

  return microserviceAxiosInstance.post(`/${company}/pf/receipt`, formData, {
    params,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const GetPFReceiptsAPI = (branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/pf/receipt`, { params });
};

export const AddPFResponseAPI = (data, branchId) => {
  const companyName = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;

  return microserviceAxiosInstance.post(`/${companyName}/pf/response`, body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const GetPFResponsesAPI = (month, year, branchId) => {
  const companyName = localStorage.getItem("companyName");

  return microserviceAxiosInstance.get(`/${companyName}/pf/response`, {
    params: {
      ...(month && { month }),
      ...(year && { year }),
      ...(branchId && { branchId })
    },
    headers: {
      "Content-Type": "application/json"
    }
  });
};

// PT Api Files
export const EmployeePTComparingAPI = (month, year, formData, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(
    `/${company}/pt/comparing`,
    formData,
    {
      params,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
};

export const SubmitPTForProcessingAPI = (month, year, file, branchId) => {
  const company = localStorage.getItem("companyName");
  const formData = new FormData();
  formData.append("file", file);
  const params = { month, year };
  if (branchId) params.branchId = branchId;

  return microserviceAxiosInstance.post(`/${company}/employees/pt`, formData, {
    params,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

export const AddPTReceiptsAPI = (data, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;

  return microserviceAxiosInstance.post(`/${company}/pt/receipt`, data, {
    params,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const GetPTReceiptsAPI = (branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/pt/receipt`, { params });
};


export const AddPTResponseAPI = (data, branchId) => {
  const companyName = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;

  return microserviceAxiosInstance.post(`/${companyName}/pt/response`, body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const GetPTResponsesAPI = (month, year, branchId) => {
  const companyName = localStorage.getItem("companyName");
  const params = {};
  if (month) params.month = month;
  if (year) params.year = year;
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${companyName}/pt/response`, { params });
};

// TDS Api Files
export const EmployeeTDSComparingAPI = (month, year, formData, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;

  return microserviceAxiosInstance.post(
    `/${company}/tds/comparing`,
    formData,
    {
      params,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
};

export const SubmitTDSForProcessingAPI = (month, year, file, branchId) => {
  const company = localStorage.getItem("companyName");
  const formData = new FormData();
  formData.append("file", file);
  const params = { month, year };
  if (branchId) params.branchId = branchId;

  return microserviceAxiosInstance.post(`/${company}/employees/tds`, formData, {
    params,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const AddTDSReceiptsAPI = (data, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(`/${company}/tds/receipt`, data, {
    params,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const GetTDSReceiptsAPI = (branchId) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/tds/receipt`, { params });
};


export const AddTDSResponseAPI = (data, branchId) => {
  const companyName = localStorage.getItem("companyName");
  const body = branchId ? { ...data, branchId } : data;

  return microserviceAxiosInstance.post(`/${companyName}/tds/response`, body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const GetTDSResponsesAPI = (month, year, branchId) => {
  const companyName = localStorage.getItem("companyName");

  return microserviceAxiosInstance.get(`/${companyName}/tds/response`, {
    params: {
      ...(month && { month }),
      ...(year && { year }),
      ...(branchId && { branchId })
    },
    headers: {
      "Content-Type": "application/json"
    }
  });
};

// GST Api Files
export const GetCompanyInvoicesAPI = (companyId, filters = {}) => {
  const params = {};
  const { customerId, year, month } = filters;

  if (customerId) params.customerId = customerId;
  if (year) params.year = year;
  if (month) params.month = month;

  return axiosInstance.get(`/company/${companyId}/invoice`, { params });
};

export const GSTComparingAPI = (month, year, formData) => {
  const companyName = localStorage.getItem("companyName");

  return microserviceAxiosInstance.post(`/${companyName}/gst/comparing`, formData, {
    params: {
      month: month,
      year: year
    },
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const RegisterGSTAccountAPI = (month, year, file) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();
  formData.append("file", file);

  return microserviceAxiosInstance.post(`/${companyName}/gst/upload`, formData, {
    params: {
      month: month,
      year: year
    },
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const AddGSTReceiptsAPI = (formData) => {
  const companyName = localStorage.getItem("companyName");
  return microserviceAxiosInstance.post(`/${companyName}/gst/receipt`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const GetGSTAccountsByYearAndMonthAPI = (month, year) => {
  const companyName = localStorage.getItem("companyName");
  return microserviceAxiosInstance.get(`/${companyName}/gst`, {
    params: {
      month: month,
      year: year
    }
  });
};

export const GetGSTReceiptsAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  const params = {};
  if (month) params.month = month;
  if (year) params.year = year;
  return microserviceAxiosInstance.get(`/${company}/gst/receipt`, { params });
};

export const AddGSTResponseAPI = (data) => {
  const companyName = localStorage.getItem("companyName");

  return microserviceAxiosInstance.post(`/${companyName}/gst/response`, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const GetGSTResponsesAPI = (month, year) => {
  const companyName = localStorage.getItem("companyName");

  return microserviceAxiosInstance.get(`/${companyName}/gst/response`, {
    params: {
      ...(month && { month }),
      ...(year && { year })
    },
    headers: {
      "Content-Type": "application/json"
    }
  });
};


export const EmployeeNoAttendanceGetAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/withoutAttendance`, {
    params: { month, year }, // Passing month and year as query params
  });
};


export const EmployeePostApi = (data) => {
  return axiosInstance.post('/employee', data);
}

// Employee data + profile photo submitted together in one multipart request so the
// backend attaches the photo using the id it generates internally — no separate
// lookup of the newly created employee's id is needed on the client.
export const EmployeePostWithPhotoApi = (data, file) => {
  const formData = new FormData();
  formData.append('employeeRequest', new Blob([JSON.stringify(data)], { type: 'application/json' }));
  formData.append('file', file);

  return axiosInstance.post('/employee', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}
export const EmployeeTaxPostApi = (data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/${company}/employee`, data);
}

export const PostEmployeeExcel = async (file, branchId) => {
  const formData = new FormData();
  formData.append('file', file);
  const companyName = localStorage.getItem("companyName");
  const params = {};
  if (branchId) params.branchId = branchId;

  try {
    const response = await axiosInstance.post(
      `/${companyName}/employees/upload`,
      formData,
      {
        params,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error uploading employee file:", error);
    throw error;
  }
};

export const PostHRMEmployeeExcel = async (file, branchId) => {
  const formData = new FormData();
  formData.append('file', file);
  const companyName = localStorage.getItem("companyName");

  try {
    const response = await axiosInstance.post(
      `/${companyName}/employees/hrm/upload?branchId=${encodeURIComponent(branchId)}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error uploading HRM employee file:", error);
    throw error;
  }
};

export const uploadEmployeeImage = (employeeId, file) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();
  formData.append('file', file);

  return axiosInstance.post(`/${companyName}/employee/${employeeId}/image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const getEmployeeImage = (employeeId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/employee/${employeeId}/image`);
};
// export const getEmployeesAccounts = (employeeId) => {
//   const companyName = localStorage.getItem("companyName");
//   return axiosInstance.get(`/${companyName}/employee/accounts`);
// };

export const CandidateToEmployeePostApi = (candidateId, data) => {
  return axiosInstance.post(`/candidate/${candidateId}`, data);
}

// Pass draft=true to stage the photo (hidden from HR until final submission);
// call with file=null and draft=false to promote a previously staged draft photo
export const CandidateImageUploadApi = (candidateId, file, draft = false) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  formData.append('draft', draft);

  return axiosInstance.post(`/${companyName}/candidate/${candidateId}/image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};


export const EmployeeGetApiById = (employeeId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/employee/${employeeId}`)
}

export const BankNamesGetApi = () => {
  return axiosInstance.get("bank/list")
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error('Error fetching company by ID:', error);
      throw error;
    });
}

export const EmployeeDeleteApiById = (employeeId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/${company}/employee/${employeeId}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error('Error fetching company by ID:', error);
      throw error;
    });
}

export const EmployeePatchApiById = (employeeId, data) => {
  return axiosInstance.patch(`/employee/${employeeId}`, data)
};

// Employee data + an optional new profile photo submitted together in one request —
// a photo-only edit is never mistaken for a no-op save, since the backend treats an
// attached photo as a real change on its own.
export const EmployeePatchWithPhotoApi = (employeeId, data, file) => {
  const formData = new FormData();
  formData.append('employeeUpdateRequest', new Blob([JSON.stringify(data)], { type: 'application/json' }));
  if (file) {
    formData.append('file', file);
  }

  return axiosInstance.patch(`/employee/${employeeId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const downloadEmployeesFileAPI = async (
  format,
  selectedFields,
  showToast,
  departmentId = "",
  statuses = [],
  branchId = "",
  hiringYearFrom = "",
  hiringMonthFrom = "",
  hiringYearTo = "",
  hiringMonthTo = ""
) => {
  const company = localStorage.getItem("companyName");
  try {
    showToast("Downloading file...", "info"); // Show info toast before downloading

    let apiUrl = `${company}/employees/download?format=${format}`;
    if (branchId) {
      apiUrl += `&branchId=${branchId}`;
    }
    if (departmentId) {
      apiUrl += `&deptId=${departmentId}`;
    }

    const body = { selectedFields, statuses };
    if (hiringYearFrom) body.hiringYearFrom = hiringYearFrom;
    if (hiringMonthFrom) body.hiringMonthFrom = hiringMonthFrom;
    if (hiringYearTo) body.hiringYearTo = hiringYearTo;
    if (hiringMonthTo) body.hiringMonthTo = hiringMonthTo;

    const response = await axiosInstance.post(
      apiUrl,
      body,
      {
        responseType: "blob",
      }
    );

    // Check if response is an error by trying to parse JSON from Blob
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      // Convert Blob to JSON
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);

      if (errorJson?.error?.message) {
        throw new Error(errorJson.error.message); // Throw extracted API error message
      }
    }

    // If the response is valid, proceed with the download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Employees_data.${format === "excel" ? "xlsx" : "pdf"}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showToast("Download successful!", "success"); // Show success toast
  } catch (error) {
    let errorMessage = "Download failed. Please try again.";

    if (error.response) {
      const errorBlob = error.response.data;
      try {
        const errorText = await errorBlob.text();
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson?.error?.message || errorMessage;
      } catch (jsonError) {
        console.error("Failed to parse error response:", jsonError);
      }
    } else {
      errorMessage = error.message;
    }

    showToast(errorMessage, "error");
  }
};

export const downloadEmployeeBankDataAPI = async (format, showToast) => {
  const company = localStorage.getItem("companyName")

  try {
    showToast("Downloading file...", "info"); // Show info toast before downloading

    const response = await axiosInstance.get(`${company}/employees/bank?format=${format}`, {
      responseType: "blob",
    });

    // Check if response is an error by trying to parse JSON from Blob
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      // Convert Blob to JSON
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);

      if (errorJson?.error?.message) {
        throw new Error(errorJson.error.message); // Throw extracted API error message
      }
    }

    // If the response is valid, proceed with the download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Employees_Bank_Data.${format === "excel" ? "xlsx" : "pdf"}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showToast("Download successful!", "success"); // Show success toast
  } catch (error) {
    let errorMessage = "Download failed. Please try again.";

    if (error.response) {
      const errorBlob = error.response.data;
      try {
        const errorText = await errorBlob.text();
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson?.error?.message || errorMessage;
      } catch (jsonError) {
        console.error("Failed to parse error response:", jsonError);
      }
    } else {
      errorMessage = error.message;
    }

    showToast(errorMessage, "error");
  }
};

export const downloadAttendanceFileAPI = async (format, year, month, employeeId, showToast) => {
  const company = localStorage.getItem("companyName");

  if (!format) {
    showToast("⚠️ Please select a file format!", "warning");
    return;
  }

  try {
    const response = await axiosInstance.get(`${company}/employee/attendance/download`, {
      params: { format, month: month || "", year: year || "", employeeId: employeeId || "" },
      responseType: "blob", // Response is expected as a file, but errors can still be in JSON
    });

    // Check if response is an error by trying to parse JSON from Blob
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      // Convert Blob to JSON
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);

      if (errorJson?.error?.message) {
        throw new Error(errorJson.error.message); // Throw extracted API error message
      }
    }

    // If no error, proceed with file download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Employees_Attendance_data.${format === "excel" ? "xlsx" : "pdf"}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showToast("✅ Download successful!", "success");
  } catch (error) {
    console.error("Error downloading file:", error);

    // Extract API error message properly
    let errorMessage = "Download failed. Please try again.";

    if (error.response) {
      const errorBlob = error.response.data;
      try {
        const errorText = await errorBlob.text();
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson?.error?.message || errorMessage;
      } catch (jsonError) {
        console.error("Failed to parse error response:", jsonError);
      }
    } else {
      errorMessage = error.message;
    }

    showToast(errorMessage, "error");
  }
};

export const EmployeeSalaryPostApi = (employeeId, data) => {
  return axiosInstance.post(`/${employeeId}/salary`, data, {
    headers: {
      'Content-Type': 'application/json', // Ensure content type is JSON
    }
  });
}

export const EmployeeSalaryGetApi = (employeeId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/employee/${employeeId}/salaries`);
}

export const EmployeesSalariesGetApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/employee/salaries`);
}

export const EmployeeSalaryGetApiById = (employeeId, salaryId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/employee/${employeeId}/salary/${salaryId}`);
}

export const EmployeeSalaryPatchApiById = (employeeId, salaryId, data) => {
  return axiosInstance.patch(`/employee/${employeeId}/salary/${salaryId}`, data);
}

export const EmployeeSalaryDeleteApiById = (employeeId, salaryId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/${company}/employee/${employeeId}/salary/${salaryId}`);
}

// export const EmployeesAccountGetAll = () => {
//   const company = localStorage.getItem("companyName")
//   return axiosInstance.get(`/${company}/employee/accounts`);
// }

export const downloadEmployeeSalaryDataAPI = async (format, selectedFields, showToast) => {
  const company = localStorage.getItem("companyName");

  try {
    showToast("Downloading file...", "info"); // Show info toast before downloading

    const response = await axiosInstance.post(
      `${company}/employee/salaries/download?format=${format}`,
      { selectedFields },
      {
        responseType: "blob",
      }
    );

    // Check if response is an error by trying to parse JSON from Blob
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);

      if (errorJson?.error?.message) {
        throw new Error(errorJson.error.message);
      }
    }

    // Proceed with file download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Employees_Salaries_Data.${format === "excel" ? "xlsx" : "pdf"}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showToast("Download successful!", "success");
  } catch (error) {
    let errorMessage = "Download failed. Please try again.";

    if (error.response) {
      const errorBlob = error.response.data;
      try {
        const errorText = await errorBlob.text();
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson?.error?.message || errorMessage;
      } catch (jsonError) {
        console.error("Failed to parse error response:", jsonError);
      }
    } else {
      errorMessage = error.message;
    }

    showToast(errorMessage, "error");
  }
};



export const EmployeePayslipGenerationPostById = (employeeId, salaryId, data) => {
  return axiosInstance.post(`/${employeeId}/salary/${salaryId}`, data);
}

export const EmployeePayslipResponse = (salaryId, data) => {
  // Build the query string manually if salaryId is present
  const url = salaryId ? `/payslip?salaryId=${salaryId}` : '/payslip';

  // Make the axios request
  return axiosInstance.post(url, data);
};

export const EmployeePayslipGeneration = (data) => {
  return axiosInstance.post("/salary", data);
}

export const EmployeePayslipUpdate = (employeeId, payslipId, payload) => {
  return axiosInstance.put(`/employee/${employeeId}/payslip/${payslipId}`, payload);
}

// Creates the payslip using the edited salary sent in the payload. The payslipId is
// derived deterministically from month/year/employeeId, so it is already known from the
// generation preview before the payslip exists.
export const EmployeePayslipSave = (employeeId, payslipId, payload) => {
  return axiosInstance.post(`/employee/${employeeId}/payslip/${payslipId}`, payload);
}

export const EmployeePayslipGetById = (employeeId, payslipId, month, year) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/employee/${employeeId}/payslip/${payslipId}`, {
    params: {
      month: month,
      year: year
    }
  });
};


export const EmployeePayslipsGet = (employeeId, month, year) => {
  const company = localStorage.getItem("companyName");
  const params = { year };

  // Only add month to params if it's not empty
  if (month) {
    params.month = month;
  }

  return axiosInstance.get(`/${company}/employee/${employeeId}/payslips`, {
    params
  });
}

export const AllEmployeePayslipsGet = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { year };

  // Only add month to params if it's not empty
  if (month) {
    params.month = month;
  }
  if (branchId) {
    params.branchId = branchId;
  }

  return axiosInstance.get(`/${company}/employee/all/payslip`, {
    params
  });
}

export const EmployeePaySlipDownloadById = async (employeeId, payslipId) => {
  const company = localStorage.getItem("companyName");
  console.log("companyName:", company);
  
  try {
    // Make the API request with specific headers for this request
    const response = await axiosInstance.get(`/${company}/employee/${employeeId}/download/${payslipId}`, {
      responseType: 'blob', // Handle the response as a binary blob
      headers: {
        'Accept': 'application/json', // Change from application/pdf to application/json
      }
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `payslip.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};


export const EmployeePayslipDeleteById = (employeeId, payslipId) => {
  const company = localStorage.getItem("comapnyName")
  return axiosInstance.delete(`/${company}/employee/${employeeId}/payslip/${payslipId}`);
}

export const AttendanceManagementApi = (formData) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.post(`/${company}/employee/attendance`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export const AttendanceReportApi = (employeeId, month, year) => {

  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/attendance`, {
    params: { employeeId, month, year }
  });
}

export const AttendancePatchById = (employeeId, attendanceId, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.patch(`/${company}/employee/${employeeId}/attendance/${attendanceId}`, data);
}

export const AttendanceDeleteById = (employeeId, attendanceId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/${company}/employee/${employeeId}/attendance/${attendanceId}`);
}

export const CompanyImagePatchApi = (companyId, formData) => {
  return axiosInstance.patch(`/company/image/${companyId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const CompanyStampPatchApi = (companyId, formData) => {
  return axiosInstance.patch(`/company/stampImage/${companyId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const CompanyImageGetApi = (companyId) => {
  return axiosInstance.get(`/company/${companyId}/image`);
}

export const AllowancesGetApi = () => {
  return axiosInstance.get(`/allowances`);
}

export const DeductionsGetApi = () => {
  return axiosInstance.get(`/deductions`);
}

export const CompanySalaryStructurePostApi = (data) => {
  return axiosInstance.post(`/salary/Structure`, data);
};

export const CompanySalaryStructurePatchApi = (id, data) => {
  return axiosInstance.patch(`/salary/Structure/${encodeURIComponent(id)}`, data);
};

export const CompanySalaryStructureGetApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`${company}/salary`);
}

export const PayslipTemplate = (data) => {
  return axiosInstance.patch(`/template`, data);
};

export const TemplateSelectionPatchAPI = (data) => {
  return axiosInstance.patch(`/template`, data);
};

export const TemplateGetAPI = (data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/template`, data);
};

export const RelievingDownloadPostApi = (employeeId, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.post(`/${company}/employee/${employeeId}/download`, data);
}
export const RelievingFormPostApi = (employeeId, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.post(`/company/${company}/employee/${employeeId}/relieving`, data);
}

export const RelievingGetApiById = (employeeId) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/relieving/${employeeId}`);
}

export const RelievingGetAllApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/relieving`);
}

export const RelievingDeleteApiById = (employeeId, id) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/${company}/employee/${employeeId}/relieve/${id}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error('Error fetching company by ID:', error);
      throw error;
    });
}

export const RelievingPatchApiById = (employeeId, relieveId, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.patch(`/${company}/employee/${employeeId}/relieve/${relieveId}`, data)
};

export const RelievingLetterDownload = async (employeeId, draft = false) => {
  const company = localStorage.getItem("companyName")
  try {
    // The endpoint reads companyName + employeeId from the path and draft from the query
    // string; it declares no @RequestBody, so there is nothing to send as a body.
    const response = await axiosInstance.post(`/${company}/employee/${employeeId}/download?draft=${draft}`, null, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      }
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "RelievingLetter.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export const ExperienceFormPostApi = async (payload) => {
  try {
    const response = await axiosInstance.post(`/experienceletter/upload`, JSON.stringify(payload), // Ensure JSON format
      {
        responseType: 'blob', // Handle binary response for file download
        headers: {
          'Content-Type': 'application/json', // Correct content type
          'Accept': 'application/pdf',        // Expected response format
        },
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "ExperienceLetter.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export const OfferLetterDownload = async (payload) => {
  try {
    const response = await axiosInstance.post(`/offerletter/upload`, payload, {
      responseType: "blob", // Required to handle file download
    }
    );
    // Create a blob URL and download it
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "OfferLetter.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    // ✅ Check if response data is a Blob (when responseType is 'blob')
    if (error.response?.data instanceof Blob) {
      try {
        // Convert blob to text, then to JSON
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        // Add this to the error object so it can be used outside
        error.parsedMessage = json?.error?.message;
      } catch (parseError) {
        console.warn("Failed to parse error blob:", parseError);
      }
    }

    console.error("Download error:", error);
    throw error;
  }
};

const attachDownloadErrorMessage = async (error) => {
  if (!(error.response?.data instanceof Blob)) return error;

  try {
    const text = await error.response.data.text();
    let message = "";

    try {
      const json = JSON.parse(text);
      message = json?.error?.message || json?.message || json?.error || "";
    } catch {
      message = text
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    if (message) error.parsedMessage = message;
  } catch (parseError) {
    console.warn("Failed to parse download error blob:", parseError);
  }

  return error;
};
export const InternOfferLetterDownload = async (payload) => {
  try {
    const response = await axiosInstance.post(`/internShipLetter/download`, payload, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf, application/json',
      }
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "InternOfferLetter.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return true;

  } catch (error) {
    await attachDownloadErrorMessage(error);
    console.error('Download error:', error);
    throw error;
  }
};



export const InternshipCertificateDownload = async (payload) => {
  try {
    const response = await axiosInstance.post(`/internship/upload`, payload, {
      responseType: 'blob', // Handle binary response for file download
      headers: {
        'Content-Type': 'application/json', // Correct content type
        'Accept': 'application/pdf',        // Expected response format
      },
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "InternshipCertificate.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};


export const AppraisalLetterDownload = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `/appraisal/upload`,
      JSON.stringify(payload), // Ensure JSON format
      {
        responseType: 'blob', // Handle binary response for file download
        headers: {
          'Content-Type': 'application/json', // Correct content type
          'Accept': 'application/pdf',        // Expected response format
        },
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // getting filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "AppraisalLetter.pdf"; // default
    if (contentDisposition && contentDisposition.indexOf("filename=") !== -1) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) fileName = match[1];
    }

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error.response || error.message);
    throw error; // Re-throw for handling by calling code
  }
};

// ── Employee Documents module (relieved-employee document hand-off) ─────────

export const AppraisalHistoryGetApi = (companyId, employeeId) => {
  return axiosInstance.get(`/${companyId}/appraisal/employee/${employeeId}`);
};

export const ExperienceHistoryGetApi = (employeeId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/experienceletter/employee/${employeeId}`);
};

export const DocumentsSendApi = (employeeId, useOriginalTemplate) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/${company}/documents/${employeeId}/send`, null, {
    params: { useOriginalTemplate },
  });
};

// Blob-returning fetches for View/Download — these render with the company's
// current template (same as every other standalone download in the app); the
// Current/Original choice only applies when actually sending by email.
export const PayslipDocumentGetApi = (employeeId, payslipId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/employee/${employeeId}/download/${payslipId}`, {
    responseType: "blob",
  });
};

export const RelievingDocumentGetApi = (employeeId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/${company}/employee/${employeeId}/download?draft=false`, {}, {
    responseType: "blob",
  });
};

export const AppraisalDocumentGetApi = (companyId, employeeId, appraisalId) => {
  return axiosInstance.get(`/${companyId}/appraisal/employee/${employeeId}/${appraisalId}/download`, {
    responseType: "blob",
  });
};

export const ExperienceDocumentGetApi = (employeeId, experienceId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/experienceletter/employee/${employeeId}/${experienceId}/download`, {
    responseType: "blob",
  });
};

export const CustomerGetAllApi = (companyId) => {
  return axiosInstance.get(`/company/${companyId}/customer/all`);
};

export const CustomerPostApi = async (companyId, data) => {
  try {
    const response = await axiosInstance.post(`/company/${companyId}/customer`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

export const CustomerGetApiById = async (companyId, customerId) => {
  try {
    const response = await axiosInstance.get(`/company/${companyId}/customer/${customerId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customer by ID:', error);
    throw error;
  }
};

export const CustomerDeleteApiById = async (companyId, customerId) => {
  try {
    const response = await axiosInstance.delete(`/company/${companyId}/customer/${customerId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting customer by ID:', error);
    throw error;
  }
};

export const CustomerPutApiById = async (companyId, customerId, data) => {
  try {
    const response = await axiosInstance.patch(`/company/${companyId}/customer/${customerId}`, data, {
      headers: {
        "Content-Type": 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating customer by ID:', error);
    throw error;
  }
};

export const ProductGetAllApi = (companyId) => {
  return axiosInstance.get(`/company/${companyId}/product/all`);
};

export const ProductPostApi = (companyId, data) => {
  return axiosInstance.post(`/company/${companyId}/product`, data)
    .then(response => response.data)
    .catch(error => {
      console.error('Error creating product:', error);
      throw error;
    });
};

export const ProductGetApiById = (companyId, productId) => {
  return axiosInstance.get(`/company/${companyId}/product/${productId}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching product by ID:', error);
      throw error;
    });
};

export const ProductDeleteApiById = (companyId, productId) => {
  return axiosInstance.delete(`/company/${companyId}/product/${productId}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error deleting product by ID:', error);
      throw error;
    });
};

export const ProductPutApiById = (companyId, productId, data) => {
  return axiosInstance.patch(`/company/${companyId}/product/${productId}`, data, {
    headers: {
      "Content-Type": 'application/json'
    }
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Error updating product by ID:', error);
      throw error;
    });
};

// Bank Get All API
export const BankGetAllApi = (companyId) => {
  return axiosInstance.get(`/company/${companyId}/bank`)
  // .then(response => response.data)
  // .catch(error => {
  //   console.error('Error fetching all banks:', error);
  //   throw error;
  // });
};

// Bank Post API (Create a new bank)
export const BankPostApi = (companyId, data) => {
  return axiosInstance.post(`/company/${companyId}/bank`, data)
    .then(response => response.data)
    .catch(error => {
      console.error('Error creating bank:', error);
      throw error;
    });
};

// Bank Get API by ID
export const BankGetApiById = (companyId, bankId) => {
  return axiosInstance.get(`/company/${companyId}/bank/${bankId}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching bank by ID:', error);
      throw error;
    });
};

// Bank Delete API by ID
export const BankDeleteApiById = (companyId, bankId) => {
  return axiosInstance.delete(`/company/${companyId}/bank/${bankId}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error deleting bank by ID:', error);
      throw error;
    });
};

// Bank Patch API by ID (Update a bank)
export const BankPutApiById = (companyId, bankId, data) => {
  return axiosInstance.patch(`/company/${companyId}/bank/${bankId}`, data, {
    headers: {
      "Content-Type": 'application/json'
    }
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Error updating bank by ID:', error);
      throw error;
    });
};

export const InvoicePostApi = (companyId, customerId, data) => {
  // Validate inputs
  if (!companyId || !customerId) {
    console.error('Missing required parameters:', { companyId, customerId });
    return Promise.reject(new Error('Missing companyId or customerId'));
  }

  return axiosInstance.post(
    `/company/${encodeURIComponent(companyId)}/customer/${encodeURIComponent(customerId)}/invoice`,
    data
  )
    .then(response => response.data)
    .catch(error => {
      console.error('Error creating invoice:', error);
      throw error;
    });
};

export const InvoiceGetAllApi = (companyId, customerId = null, branchId = null) => {
  const params = {};
  if (customerId) {
    params.customerId = customerId;
  }
  if (branchId) {
    params.branchId = branchId;
  }
  return axiosInstance.get(`/company/${companyId}/invoice`, { params });
};

export const InvoiceGetByCustomerIdApi = (companyId, customerId) => {
  return axiosInstance.get(`/company/${companyId}/customer/${customerId}/invoice`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching product by ID:', error);
      throw error;
    });
};

export const InvoiceGetApiById = (companyId, customerId, invoiceId) => {
  return axiosInstance.get(`/company/${companyId}/customer/${customerId}/invoice/${invoiceId}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching product by ID:', error);
      throw error;
    });
};

export const InvoiceDownloadById = async (companyId, customerId, invoiceId) => {
  try {
    // Make the API request with specific headers for this request
    const response = await axiosInstance.get(`/company/${companyId}/customer/${customerId}/downloadInvoice/${invoiceId}`, {
      responseType: 'blob', // Handle the response as a binary blob (PDF)
      headers: {
        'Accept': 'application/pdf',  // Indicate that we expect a PDF
      },
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));

    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice_${customerId}_${invoiceId}.pdf`; // Customize the filename
    document.body.appendChild(link);
    link.click(); // Trigger the download
    document.body.removeChild(link); // Clean up the link element

    // Optionally revoke the URL to free up memory
    window.URL.revokeObjectURL(url);

    return true;  // Indicate success
  } catch (error) {
    console.error('Download error:', error);
    return false;  // Indicate failure
  }
};
export const DialCodesListApi = () => {
  return axiosInstance.get(`/dialcodes/list`);
}
export const TdsGetApi = () => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/company/${company}/tds`);
};
export const TdsPostApi = (data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/company/${company}/tds`, data, {
    headers: { "Content-Type": "application/json" },
  });
};
export const TdsPatchApi = (id, data) => {
  const company = localStorage.getItem("companyName"); // Retrieve company name
  return axiosInstance.patch(`/company/${company}/tds/${id}`, data, {
    headers: { "Content-Type": "application/json" },
  });
};
export const getCompanyTdsByYear = (year) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/company/${company}/tds/${year}/year`);
};

export const calendarPostAPI = async (data) => {
  const company = localStorage.getItem("companyName")
  try {
    const response = await axiosInstance.post(`/company/${company}/calendar`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

export const CalendarGetApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/company/${company}/calendar`);
}

export const CalendarGetApiById = (id) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/company/${company}/calendar/${id}`);
}

export const TodayCalendarGetApi = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/company/${company}/calendar/today`);
}

export const calendarPatchAPIById = async (data, id) => {
  const company = localStorage.getItem("companyName")
  try {
    const response = await axiosInstance.patch(`/company/${company}/calendar/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

export const CalendarDeleteByIdApi = async (id) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/company/${company}/calendar/${id}`);
};

export const UserGetApi = () => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/users`);
};
export const UserPostApi = (data) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.post(`/${company}/user`, data);
};
export const UserPatchApi = (id, data) => {
  const company = localStorage.getItem("companyName"); // Retrieve company name
  return axiosInstance.patch(`/${company}/user/${id}`, data);
};
export const getSubcriptionsByCompany = () => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/subscriptions`);
};

export const EmployeeSaveDraftApi = (data) => {
  return axiosInstance.post(`/employee/draft`, data);
};

export const EmployeeUpdateDraftApi = (draftId, data) => {
  return axiosInstance.patch(`/employee/draft/${draftId}`, data);
};

export const EmployeeDraftsGetApi = (companyName, createdBy) => {
  let url = `/${companyName}/employee/drafts`;
  if (createdBy) {
    url += `?createdBy=${encodeURIComponent(createdBy)}`;
  }
  return axiosInstance.get(url);
};

export const EmployeeDraftDeleteApi = (draftId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${company}/employee/draft/${draftId}`);
};

export const getUserById = (id) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/${company}/user/${id}`);
};
export const DeleteUserById = (id) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${company}/user/${id}`);
};

// Candidate apis
export const CandidatePostApi = async (candidateData) => {
  try {
    const response = await axiosInstance.post("/candidate", candidateData);

    // Return the full response object
    return {
      status: response.status,
      data: response.data,
      fullResponse: response
    };
  } catch (error) {
    console.error('API Error:', {
      config: error.config,
      response: error.response,
      message: error.message
    });

    // Return a consistent error structure
    return {
      status: error.response?.status || 500,
      error: true,
      message: error.response?.data?.message || error.message,
      fullResponse: error.response
    };
  }
};
export const CandidateGetAllApi = () => {
  const company = localStorage.getItem("companyName");
  if (!company) {
    throw new Error("Company Name is not found");
  }

  return axiosInstance.get(`/candidate/${company}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }
  });
};

export const CandidateGetByIdApi = (candidateId) => {
  const company = localStorage.getItem("companyName");
  return axiosInstance.get(`/candidate/${company}/${candidateId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }
  });
};


export const CandidateDeleteApi = (id) => {  // Changed parameter name to be more clear
  const company = localStorage.getItem("companyName");
  if (!company) {
    throw new Error("Company Name is not found");
  }

  return axiosInstance.delete(`/${company}/candidate/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Delete Candidate Error:', {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    });
};

export const updateCandidate = (candidateId, candidateData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.put(
    `/candidate/${companyName}/${candidateId}`,
    candidateData
  );
};


export const uploadDocumentAPI = async (candidateId, docNames, files, isDraft = false) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();

  // Add data to FormData
  docNames.forEach((name, index) => {
    formData.append(`docNames[${index}]`, name);
    formData.append(`files[${index}]`, files[index]);
  });
  formData.append("draft", isDraft);

  try {
    const response = await axiosInstance.post(
      `/${companyName}/candidate/${candidateId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Full error details:', {
      url: error.config.url,
      method: error.config.method,
      headers: error.config.headers,
      data: error.config.data,
      response: error.response?.data
    });
    throw error;
  }
};

export const getDocumentByIdAPI = async (candidateId = '', employeeId = '') => {
  const companyName = localStorage.getItem("companyName");

  try {
    const response = await axiosInstance.get(
      `/${companyName}/documents`,
      {
        params: {
          ...(candidateId && { candidateId }),
          ...(employeeId && { employeeId })
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return null; // Gracefully handle 404
    }
    // Suppress all other errors silently
    return null;
  }
};


export const deleteDocumentByIdAPI = async (candidateId, documentId) => {
  const companyName = localStorage.getItem("companyName");

  try {
    const response = await axiosInstance.delete(
      `/${companyName}/candidate/${candidateId}/document/${documentId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', {
      url: error.config?.url,
      method: error.config?.method,
      response: error.response?.data
    });
    throw error;
  }
};

export const uploadEmployeeDocumentAPI = async (employeeId, docNames, files, isDraft = false) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();

  docNames.forEach((name, index) => {
    formData.append(`docNames[${index}]`, name);
    formData.append(`files[${index}]`, files[index]);
  });
  formData.append("draft", isDraft);

  try {
    const response = await axiosInstance.post(
      `/${companyName}/employee/${employeeId}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEmployeeDocument = (employeeId, documentId, documentNumbers, docNames, files, isDraft = false, removedFileNames = [], renamedDocuments = []) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();

  if (docNames.length !== files.length || docNames.length !== documentNumbers.length) {
    throw new Error(`Mismatched counts: ${documentNumbers.length} documentNumbers, ${docNames.length} docNames vs ${files.length} files`);
  }

  docNames.forEach((name, index) => {
    formData.append(`documentNo[${index}]`, documentNumbers[index]);
    formData.append(`docNames[${index}]`, name);
    formData.append(`files[${index}]`, files[index]);
  });
  removedFileNames.forEach((fileName, index) => {
    formData.append(`removedFileNames[${index}]`, fileName);
  });
  renamedDocuments.forEach(({ fileName, docName }, index) => {
    formData.append(`renamedFileNames[${index}]`, fileName);
    formData.append(`renamedDocNames[${index}]`, docName);
  });
  formData.append("draft", isDraft);

  return axiosInstance.patch(
    `/${companyName}/employee/${employeeId}/document/${documentId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

export const updateCandidateDocument = (candidateId, documentId, documentNumbers, docNames, files, isDraft = false, removedFileNames = [], renamedDocuments = []) => {
  const companyName = localStorage.getItem("companyName");
  const formData = new FormData();

  // Verify counts match
  if (docNames.length !== files.length || docNames.length !== documentNumbers.length) {
    throw new Error(`Mismatched counts: ${documentNumbers.length} documentNumbers, ${docNames.length} docNames vs ${files.length} files`);
  }

  // Append all documents
  docNames.forEach((name, index) => {
    formData.append(`documentNo[${index}]`, documentNumbers[index]);
    formData.append(`docNames[${index}]`, name);
    formData.append(`files[${index}]`, files[index]);
  });
  removedFileNames.forEach((fileName, index) => {
    formData.append(`removedFileNames[${index}]`, fileName);
  });
  renamedDocuments.forEach(({ fileName, docName }, index) => {
    formData.append(`renamedFileNames[${index}]`, fileName);
    formData.append(`renamedDocNames[${index}]`, docName);
  });
  formData.append("draft", isDraft);

  return axiosInstance.patch(
    `/${companyName}/candidate/${candidateId}/document/${documentId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

//gst Account
export const PostGstAccountExcel = async (formData, month, year) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(
      `/${companyName}/gst/uplaod?month=${month}&year=${year}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating GST receipt:", error);
    throw error;
  }
};

export const postGstAccountRegistration = async (data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating GST:', error);
    throw error;
  }
}

export const GstAccountGetAPI = async () => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/gst/accounts`);
    return response.data;
  } catch (error) {
    console.error('Error creating GST:', error);
    throw error;
  }
}
export const GetGstAccountByMonthYear = async (month, year) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst`,
      {
        params: { month, year }, // Passing month and year as query params
      });
    return response.data;
  } catch (error) {
    console.error('Error creating GST receipt:', error);
    throw error;
  }
}
export const getGstAccountById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/gst/account/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching GST response by ID:', error);
    throw error;
  }
}
export const deleteGstAccountById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.delete(`/${companyName}/account/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST response by ID:', error);
    throw error;
  }
}
export const putGstAccountById = async (id, data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.put(`/${companyName}/gst/account/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating GST response by ID:', error);
    throw error;
  }
}
//gst post API
export const GstPostApi = async (data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst/comparing`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating GST:', error);
    throw error;
  }
}
//gst response
export const getGstResponse = async (month, year) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/gst/response`, {
      params: { month, year }, // Passing month and year as query params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching GST response:', error);
    throw error;
  }
}
export const GstPostResponse = async (data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst/response`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}
export const getGstResponseById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/gst/response/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching GST response by ID:', error);
    throw error;
  }
}
export const deleteGstResponseById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.delete(`/${companyName}/gst/response/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST response by ID:', error);
    throw error;
  }
}
export const patchGstResponseById = async (id, data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.patch(`/${companyName}/gst/response/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating GST response by ID:', error);
    throw error;
  }
}
// gst Reciept
export const GstReceiptResponse = async (month, year) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst/receipt`,
      {
        params: { month, year }, // Passing month and year as query params
      });
    return response.data;
  } catch (error) {
    console.error('Error creating GST receipt:', error);
    throw error;
  }
}

export const postGstReceipt = async (formData) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/gst/receipt`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Ensure the correct content type for file uploads
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating GST receipt:', error);
    throw error;
  }
}

export const getGstReciptById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/gst/receipt/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching GST receipt by ID:', error);
    throw error;
  }
}

export const deleteGstReciptById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.delete(`/${companyName}/gst/receipt/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}

export const patchGstReciptById = async (id, data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.patch(`/${companyName}/gst/receipt/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating GST receipt by ID:', error);
    throw error;
  }
}
export const CredentialsGetAPI = () => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.get(`/${company}/portalCred`)
}

export const CredentialsPostAPI = (data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.post(`/${company}/portalCred`, data);
}

export const CredentialsPatchAPIById = (id, data) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.patch(`/${company}/portalCred/${id}`, data)
}

export const CredentialsDeleteAPIById = (id) => {
  const company = localStorage.getItem("companyName")
  return axiosInstance.delete(`/${company}/portalCred/${id}`)
}

//Time Lines
export const postDueDates = async (data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.post(`/${companyName}/dates`, data);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}
export const getDueDates = async () => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/dates`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}

export const getDueDatesStatus = async () => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/taxStatus`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}
export const patchDueDatesById = async (id, data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.patch(`/${companyName}/date/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}
export const deleteDueDatesById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.delete(`/${companyName}/dates/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting GST receipt by ID:', error);
    throw error;
  }
}

export const HRMEmployeePTComparingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/pt/comparing`, { params });
};

export const HRMEmployeePFComparingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/pf/comparing`, { params });
};
export const HRMSubmitPFForProcessingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(`/${company}/pf`, null, { params });
};

export const HRMSubmitPTForProcessingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(`/${company}/pt`, null, { params });
};

export const HRMEmployeeTDSComparingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.get(`/${company}/tds/comparing`, { params });
};
export const HRMSubmitTDSForProcessingAPI = (month, year, branchId) => {
  const company = localStorage.getItem("companyName");
  const params = { month, year };
  if (branchId) params.branchId = branchId;
  return microserviceAxiosInstance.post(`/${company}/tds`, null, { params });
};
export const HRMGSTComparingAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  return microserviceAxiosInstance.get(`/${company}/account/comparing`, {
    params: { month, year }
  });
};
export const HRMSubmitGSTForSubmissionAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  return microserviceAxiosInstance.post(`/${company}/gst/comparing`, {
    params: { month, year }
  });
};
export const HRMSubmitGSTForResponseAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  return microserviceAxiosInstance.post(`/${company}/gst/response`, {
    params: { month, year }
  });
};

export const HRMRegisterGSTAccountAPI = (month, year) => {
  const company = localStorage.getItem("companyName");
  return microserviceAxiosInstance.post(
    `/${company}/gst/register?month=${month}&year=${year}`,
  );
};

// Leave Management APIs Leave Type
export const addCompanyLeave = (leaveData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.post(`/${companyName}/leave`, leaveData);
};

export const getLeaveTypes = () => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/leave`);
};

export const getLeaveTypeById = (id) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/leave/${id}`);
};

export const deleteLeaveType = (id) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${companyName}/leave/${id}`);
};

export const updateLeaveType = (id, leaveData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.patch(`/${companyName}/leave/${id}`, leaveData);
};

// Leave Management APIs Reason Type api's

export const getReasonById = (leaveId, reasonId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/leave/${leaveId}/reason/${reasonId}`);
};

export const addCompanyLeaveReason = (leaveId, reasonData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.post(`/${companyName}/leave/${leaveId}/reason`, reasonData);
};

export const getReasonsByLeaveId = (leaveId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/leave/${leaveId}/reason`);
};

export const getAllCompanyReasons = () => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/reasons`);
};

export const updateReasonById = (leaveId, reasonId, reasonData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.put(`/${companyName}/leave/${leaveId}/reason/${reasonId}`, reasonData);
};

export const deleteReasonById = (leaveId, reasonId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${companyName}/leave/${leaveId}/reason/${reasonId}`);
};

// employee leave APIs
export const applyLeaveForEmployee = (employeeId, leaveData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.post(`/${companyName}/employee/${employeeId}/leave`, leaveData);
};
export const getEmployeeLeaves = (employeeId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/employee/${employeeId}/leave`);
};

export const getEmployeeLeaveById = (employeeId, leaveId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/employee/${employeeId}/leave/${leaveId}`);
};
export const updateEmployeeLeave = (employeeId, leaveId, updatedLeaveData) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.patch(`/${companyName}/employee/${employeeId}/leave/${leaveId}`, updatedLeaveData);
};
export const deleteEmployeeLeave = (employeeId, leaveId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.delete(`/${companyName}/employee/${employeeId}/leave/${leaveId}`);
};

// all leaves API
export const getAllLeaves = () => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/leaves`);
};
export const getEmployeeLeaveSummaryById = (employeeId, leaveTypeId, year, month) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/employee/${employeeId}/leaveType/${leaveTypeId}`, {
    params: {
      year,
      ...(month && { month }) // Only include month if it's provided
    }
  });
};
export const GetEmployeeLeavesByManagerId = (employeeId) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.get(`/${companyName}/manager/${employeeId}`);
};

export const updateLeaveStatus = (employeeId, leaveId, leaveStatus) => {
  const companyName = localStorage.getItem("companyName");
  return axiosInstance.patch(
    `/${companyName}/employee/${employeeId}/leave/${leaveId}/status`,
    { leaveStatus }
  );
};

// Password Manager Api files

export const postPortalDetails = async (data, branchId) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const params = {};
    if (branchId) params.branchId = branchId;
    const response = await microserviceAxiosInstance.post(`/${companyName}/portalCred`, data, { params });
    return response.data;
  } catch (error) {
    console.error('Error adding portal details:', error);
    throw error;
  }
};

export const getPortalDetails = async (branchId) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const params = {};
    if (branchId) params.branchId = branchId;
    const response = await microserviceAxiosInstance.get(`/${companyName}/portalCred`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching portal details:', error);
    throw error;
  }
};

export const getPortalDetailsById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.get(`/${companyName}/portalCred/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching portal details for ID ${id}:`, error);
    throw error;
  }
};

export const updatePortalDetailsById = async (id, data) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.patch(`/${companyName}/portalCred/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating portal details for ID ${id}:`, error);
    throw error;
  }
};

export const deletePortalDetailsById = async (id) => {
  const companyName = localStorage.getItem("companyName");
  try {
    const response = await microserviceAxiosInstance.delete(`/${companyName}/portalCred/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting portal details for ID ${id}:`, error);
    throw error;
  }
};

export const GetLastInvoiceNumberAPI = (companyId) => {
  return axiosInstance.get(`/company/${companyId}/invoice/last`);
};

//Default Settings Api
export const DefaultSettingsGetApi = () => {
  return axiosInstance.get(`/default`);
};

export const DefaultSettingsPostApi = (data) => {
  return axiosInstance.post(`/default/add`, data);
};

export const DefaultSettingsPatchApi = (data) => {
  return axiosInstance.put(`/default/add`, data);
};

// ─── Custom Template APIs ─────────────────────────────────────────────────────

export const CreateCustomTemplateApi = (companyName, data) =>
  axiosInstance.post(`/${companyName}/custom-template`, data);

export const GetCustomTemplateByModuleApi = (companyName, module) =>
  axiosInstance.get(`/${companyName}/custom-template/${module}`);

export const UpdateCustomTemplateApi = (companyName, id, data) =>
  axiosInstance.patch(`/${companyName}/custom-template/${id}`, data);
